import {
  Box,
  Avatar,
  Text,
  Tooltip,
  type AvatarProps,
  HoverCard,
} from '@mantine/core'
import Link from 'next/link'

import { useTranslation } from '@flecto/translations'
import { type CoreItem } from '@flecto/types'

import { Badge, AvatarItem, BadgeItemInsurance } from '..'
import classes from './styles.module.css'

export type AvatarItemsGroupProps = Pick<AvatarProps, 'size' | 'radius'> & {
  items?: CoreItem[]
  withLimit?: number
}

export function AvatarItemsGroup({
  items,
  radius,
  size = 'md',
  withLimit = 4,
}: AvatarItemsGroupProps) {
  const { t } = useTranslation('shared', { keyPrefix: 'shared' })
  const localItems = (items ?? []).slice(0, withLimit)
  const exceedingItems =
    (items ?? []).length - withLimit > 0
      ? (items ?? []).slice(((items ?? []).length - withLimit) * -1)
      : []

  if (localItems.length <= 0) {
    return (
      <Badge color='gray' data-testid='no-items'>
        {t('label_no_items')}
      </Badge>
    )
  }

  return (
    <Avatar.Group spacing='sm'>
      {localItems.map((item) => (
        <Tooltip
          key={`avatar-item-${item?.id}`}
          label={
            <Text truncate>
              {item?.quantity}x {item?.title}
            </Text>
          }
          maw={250}
          withinPortal
        >
          <Box>
            <AvatarItem item={item} size={size} />
          </Box>
        </Tooltip>
      ))}
      {exceedingItems.length > 0 && (
        <HoverCard position='bottom'>
          <HoverCard.Target>
            <Avatar size='md' radius={radius}>
              <Text fw={500} size='sm'>
                +{exceedingItems.length}
              </Text>
            </Avatar>
          </HoverCard.Target>
          <HoverCard.Dropdown
            miw={300}
            py='md'
            onClick={(e) => e.stopPropagation()}
          >
            {exceedingItems.map((item, i) => (
              <Box
                key={`exceeding-item-${item?.id}`}
                className={classes.item}
                mt={i > 0 ? 'md' : 0}
              >
                <AvatarItem item={item} className={classes.avatar} />
                <Link
                  href='/items'
                  onClick={(e) => e.stopPropagation()}
                  className={classes.title}
                >
                  <Text maw={150} fw={500} truncate>
                    {item.title}
                  </Text>
                  <BadgeItemInsurance item={item} />
                </Link>
                <Text className={classes.id}>ID: {item.internal_id}</Text>
                <Box className={classes.quantity}>x{item.quantity}</Box>
              </Box>
            ))}
          </HoverCard.Dropdown>
        </HoverCard>
      )}
    </Avatar.Group>
  )
}
